import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/atoms/buttons/Button';
import { FileUploadItem } from '@/components/molecules/files/FileUpload';
import {
  matchesAcceptToken,
  toFileList,
  useDropZone,
} from '@/hooks/use-drop-zone';
import { createSafeContext } from '@/lib/create-safe-context';
import { cn } from '@/lib/utils';
import { analyzePaste, mapKindToFile, type PasteKind } from './paste-detection';

// =============================================================================
// Shared Styles
// =============================================================================

const fileDropZoneA11yProps = {
  role: 'group',
  'aria-label': 'Chat input with file drop zone',
} as const;

const textareaClasses =
  'flex-1 h-[40px] resize-none overflow-y-auto bg-transparent text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:text-muted-foreground';

const compactTextareaClasses =
  'flex-1 h-[32px] resize-none overflow-y-auto bg-transparent text-base leading-6 text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:text-muted-foreground';

// =============================================================================
// Variants
// =============================================================================

const inputVariants = cva(
  'flex items-center w-full transition-[color,background-color,border-color,box-shadow] duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none',
  {
    variants: {
      state: {
        default: '',
        focused: '',
        withText: '',
        disabled: 'cursor-not-allowed',
        attachments: '',
        dragover: '',
      },
      variant: {
        default: 'flex-col gap-2 py-4 px-4 rounded-[inherit]',
        compact: 'flex-row gap-2 py-4 px-4 rounded-[inherit]',
        inline: 'flex-row gap-2 px-4 py-4 rounded-[inherit]',
      },
    },
    defaultVariants: {
      state: 'default',
      variant: 'default',
    },
  },
);

function getAIInputShellClassName({
  state,
  variant,
  className,
}: {
  state: VariantProps<typeof inputVariants>['state'];
  variant: 'default' | 'compact' | 'inline';
  className?: string;
}) {
  const isDefault = variant === 'default';
  const isDisabled = state === 'disabled';
  const isPrimaryState =
    state === 'focused' ||
    state === 'withText' ||
    state === 'attachments' ||
    state === 'dragover';
  return cn(
    'relative w-full overflow-hidden border-2 bg-background shadow-[var(--shadow-ai-input)] transition-[color,background-color,border-color,box-shadow] duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none',
    isDefault ? 'rounded-3xl max-w-[772px]' : 'rounded-2xl',
    isDisabled
      ? 'border-border bg-muted'
      : isPrimaryState
        ? 'border-primary'
        : 'border-ring/50 hover:border-primary',

    className,
  );
}

// =============================================================================
// Drop Overlay (shared across all variants)
// =============================================================================

function DropOverlay({
  isDefault,
  dropText,
  active,
}: {
  isDefault: boolean;
  dropText: string;
  active: boolean;
}) {
  return (
    <div
      data-slot="ai-input-drop-overlay"
      role="status"
      aria-live="polite"
      aria-hidden={!active}
      className={cn(
        'absolute inset-1.5 border-2 border-dashed border-primary/50 dark:border-primary/60 pointer-events-none flex flex-col items-center justify-center gap-1.5 z-10 transition-opacity duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none',
        active ? 'opacity-100' : 'opacity-0',
        isDefault ? 'rounded-[calc(1.5rem-8px)]' : 'rounded-[calc(1rem-8px)]',
      )}
    >
      <ArrowDown className="size-4 text-primary/70" />
      <span className="text-sm text-muted-foreground font-medium">
        {dropText}
      </span>
    </div>
  );
}

DropOverlay.displayName = 'AIInput.DropOverlay';

// =============================================================================
// File Attachment Types
// =============================================================================

export interface UploadingFile {
  id: string;
  name: string;
  size: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  /** File extension for icon display (e.g., 'pdf', 'docx') */
  fileExtension?: string;
}

// =============================================================================
// Paste-as-File public types
// =============================================================================

export type { PasteKind } from './paste-detection';

export interface PasteAsFileConfig {
  /** Char count above which plain text always converts. Default: 500 */
  threshold?: number;
  /** Line count above which plain text always converts. Default: 10 */
  lineThreshold?: number;
  /** Run structured-content heuristics (JSON/XML/CSV/code/log). Default: true */
  detectStructured?: boolean;
  /**
   * Auto-fill textarea after conversion. Receives filename + detected kind.
   * Return string to set as textarea value (appended with leading space if textarea non-empty).
   * Empty/whitespace-only return is treated as no-op.
   * Default: undefined → textarea stays empty.
   */
  prefillTemplate?: (filename: string, kind: PasteKind) => string;
  /** Fired after onFilesSelected and prefill. */
  onConverted?: (filename: string, kind: PasteKind) => void;
}

/**
 * Accepts boolean for runtime toggling. `false` and `undefined` both disable.
 * `true` enables with defaults. Object enables with overrides.
 */
export type PasteAsFileProp = boolean | PasteAsFileConfig;

// =============================================================================
// usePasteAsFile — internal hook
// =============================================================================

interface UsePasteAsFileOpts {
  pasteAsFile: PasteAsFileProp | undefined;
  onFilesSelected: ((files: FileList) => void) | undefined;
  accept: string | undefined;
  disabled: boolean;
  variant: 'default' | 'compact' | 'inline';
  value: string;
  setValue: (next: string) => void;
}

function usePasteAsFile(opts: UsePasteAsFileOpts): {
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
} {
  const counterRef = React.useRef(0);
  const warnedRef = React.useRef(false);
  const optsRef = React.useRef(opts);

  // Keep the ref pointing at the latest opts each render. This pattern lets the
  // returned `onPaste` be referentially stable (empty-deps useCallback) while
  // still seeing the freshest pasteAsFile config and callbacks.
  React.useEffect(() => {
    optsRef.current = opts;
  });

  const onPaste = React.useCallback<
    React.ClipboardEventHandler<HTMLTextAreaElement>
  >((e) => {
    const { pasteAsFile, onFilesSelected, accept, disabled, value, setValue } =
      optsRef.current;

    if (!pasteAsFile || disabled) return;

    const text = e.clipboardData.getData('text/plain');
    if (!text) return;

    const config: PasteAsFileConfig = pasteAsFile === true ? {} : pasteAsFile;

    const detectionOpts = {
      charThreshold: config.threshold,
      lineThreshold: config.lineThreshold,
      detectStructured: config.detectStructured,
    };

    const { shouldConvert, kind } = analyzePaste(text, detectionOpts);
    if (!shouldConvert) return;

    if (!onFilesSelected) {
      if (process.env.NODE_ENV !== 'production' && !warnedRef.current) {
        console.warn(
          '[AIInput] pasteAsFile is set but onFilesSelected is missing. Paste-to-file is a no-op.',
        );
        warnedRef.current = true;
      }
      return;
    }

    const { ext, mime } = mapKindToFile(kind);
    const nProspective = counterRef.current + 1;
    const filename = `Pasted-${nProspective}.${ext}`;

    if (
      accept &&
      !accept
        .split(',')
        .some((token) =>
          matchesAcceptToken({ name: filename, type: mime }, token),
        )
    ) {
      return;
    }

    let fileList: FileList;
    try {
      const file = new File([text], filename, { type: mime });
      fileList = toFileList([file]);
    } catch {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[AIInput] DataTransfer unavailable; falling back to native paste.',
        );
      }
      return;
    }

    e.preventDefault();
    counterRef.current = nProspective;
    onFilesSelected(fileList);

    if (config.prefillTemplate) {
      const tpl = config.prefillTemplate(filename, kind);
      if (tpl?.trim()) {
        setValue(value ? `${value} ${tpl}` : tpl);
      }
    }

    config.onConverted?.(filename, kind);
  }, []);

  if (opts.variant === 'inline' || !opts.pasteAsFile) {
    return { onPaste: undefined };
  }

  return { onPaste };
}

// =============================================================================
// Context
// =============================================================================

interface AIInputContextValue {
  /** Current input value */
  value: string;
  /** Whether the input has text */
  hasText: boolean;
  /** Whether the input is focused */
  isFocused: boolean;
  /** Whether the input is disabled */
  disabled: boolean;
  /** Visual variant */
  variant: 'default' | 'compact' | 'inline';
  /** Files being uploaded */
  uploadingFiles: UploadingFile[];
  /** Has attachments */
  hasAttachments: boolean;
  /** Update input value */
  setValue: (value: string) => void;
  /** Set focus state */
  setIsFocused: (focused: boolean) => void;
  /** Handle submit */
  handleSubmit: () => void;
  /** Handle key down */
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  /** Handle clear */
  handleClear: () => void;
  /** Paste handler from usePasteAsFile (only set when paste-as-file is active). */
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  /** Remove file */
  onRemoveFile?: (fileId: string) => void;
  /** Cancel upload */
  onCancelUpload?: (fileId: string) => void;
  /** Retry failed upload */
  onRetryUpload?: (fileId: string) => void;
  /** Remove all files */
  onRemoveAllFiles?: () => void;
  /** Whether files are being dragged over the input */
  isDragging: boolean;
  /** Open the native file picker */
  openFilePicker: () => void;
  /** Callback when files are selected (via drop or picker) */
  onFilesSelected?: (files: FileList) => void;
  /** Textarea ref (for default variant) */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Input ref (for compact variant) */
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const [AIInputContext, useAIInputContext] =
  createSafeContext<AIInputContextValue>('AIInput');

// =============================================================================
// Compound Components - Building Blocks
// =============================================================================

export interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon component to render */
  icon: React.ComponentType<{ className?: string }>;
  /** Tooltip text */
  tooltip?: string;
}

function ActionButton({
  icon: Icon,
  onClick,
  disabled,
  tooltip,
  className,
  ...props
}: ActionButtonProps) {
  return (
    <button
      type="button"
      data-slot="action-button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'p-2 rounded-full text-secondary-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    >
      <Icon className="size-4" />
    </button>
  );
}

ActionButton.displayName = 'AIInput.ActionButton';

export interface SubmitButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Icon component to render (defaults to ArrowUp) */
  icon?: React.ComponentType<{ className?: string }>;
}

function SubmitButton({
  icon: Icon = ArrowUp,
  className,
  onClick,
  disabled,
  ...props
}: SubmitButtonProps) {
  return (
    <Button
      type="button"
      data-slot="submit-button"
      size="icon-sm"
      onClick={onClick}
      disabled={disabled}
      aria-label="Send message"
      className={cn(
        'rounded-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:bg-accent disabled:text-muted-foreground transition-colors duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none',
        className,
      )}
      {...props}
    >
      <span
        data-slot="ai-input-send-icon"
        aria-hidden="true"
        className="flex size-4 shrink-0 items-center justify-center"
      >
        <Icon className="size-4" />
      </span>
    </Button>
  );
}

SubmitButton.displayName = 'AIInput.SubmitButton';

export interface CompactActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Icon component to render */
  icon: React.ComponentType<{ className?: string }>;
  /** Accessible label for the icon-only button (required for a11y) */
  'aria-label': string;
}

/**
 * @deprecated Use `ActionButton` instead. CompactActionButton will be removed in a future major version.
 */
function CompactActionButton({
  icon: Icon,
  className,
  onClick,
  disabled,
  ...props
}: CompactActionButtonProps) {
  return (
    <Button
      type="button"
      data-slot="compact-action-button"
      onClick={onClick}
      disabled={disabled}
      appearance="outline"
      variant="secondary"
      size="icon"
      className={cn(
        'rounded-full border-border text-foreground hover:bg-muted',
        className,
      )}
      {...props}
    >
      <Icon className="size-5" />
    </Button>
  );
}

CompactActionButton.displayName = 'AIInput.CompactActionButton';

function RemoveAllButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Remove all attached files"
      className="flex items-center gap-1.5 h-5 px-2 py-1 rounded-full bg-accent border border-border text-xs font-medium text-foreground hover:bg-accent/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring transition-colors"
    >
      <X className="size-4" />
      Remove all
    </button>
  );
}

RemoveAllButton.displayName = 'AIInput.RemoveAllButton';

// =============================================================================
// Render Function Types (for monolithic API)
// =============================================================================

/** Props passed to custom submit button render function */
export interface SubmitButtonRenderProps {
  onClick: () => void;
  disabled: boolean;
}

/** Render function type for custom submit button */
export type SubmitButtonRenderFn = (
  props: SubmitButtonRenderProps,
) => React.ReactElement;

/** Props passed to custom default button render function */
export interface DefaultButtonRenderProps {
  disabled: boolean;
}

/** Render function type for custom default button */
export type DefaultButtonRenderFn = (
  props: DefaultButtonRenderProps,
) => React.ReactElement;

function AIInputSubmitButton({
  customButton,
  onClick,
  disabled,
}: {
  customButton?: SubmitButtonRenderFn;
  onClick: () => void;
  disabled: boolean;
}) {
  const buttonProps: SubmitButtonRenderProps = {
    onClick,
    disabled,
  };

  if (customButton) {
    return customButton(buttonProps);
  }

  return <SubmitButton {...buttonProps} />;
}

// =============================================================================
// Composable Subcomponents (for dual-API)
// =============================================================================

export interface AIInputRootProps {
  /** Children (composable subcomponents) */
  children: React.ReactNode;
  /** Visual variant of the input */
  variant?: 'default' | 'compact' | 'inline';
  /** Current input value (controlled) */
  value?: string;
  /** Default input value (uncontrolled) */
  defaultValue?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Callback when message is submitted */
  onSubmit?: (message: string) => void;
  /** Files being uploaded */
  uploadingFiles?: UploadingFile[];
  /** Callback when file is removed */
  onRemoveFile?: (fileId: string) => void;
  /** Callback when file upload is cancelled */
  onCancelUpload?: (fileId: string) => void;
  /** Callback to retry a failed upload */
  onRetryUpload?: (fileId: string) => void;
  /** Callback to remove all files */
  onRemoveAllFiles?: () => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Callback when files are selected (via drag-drop or file picker) */
  onFilesSelected?: (files: FileList) => void;
  /** Accepted file types (e.g. 'image/*,.pdf') */
  accept?: string;
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Text shown in the drop overlay */
  dropText?: string;
  /** Convert large/structured paste into a file attachment. Requires onFilesSelected. */
  pasteAsFile?: PasteAsFileProp;
}

function AIInputRoot({
  children,
  variant = 'default',
  value: controlledValue,
  defaultValue = '',
  onChange,
  onSubmit,
  uploadingFiles = [],
  onRemoveFile,
  onCancelUpload,
  onRetryUpload,
  onRemoveAllFiles,
  disabled = false,
  className,
  onFilesSelected,
  accept,
  multiple = true,
  dropText = 'Drop files to attach',
  pasteAsFile,
}: AIInputRootProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [isFocused, setIsFocused] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const { isDragging, dropZoneProps, openFilePicker } = useDropZone({
    onFilesSelected,
    accept,
    multiple,
    disabled,
    containerRef: rootRef,
  });

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const hasText = value.trim().length > 0;
  const hasAttachments = uploadingFiles.length > 0;
  const setValue = React.useCallback(
    (newValue: string) => {
      if (onChange) {
        onChange(newValue);
      }
      if (!isControlled) {
        setInternalValue(newValue);
      }
    },
    [onChange, isControlled],
  );

  const { onPaste } = usePasteAsFile({
    pasteAsFile,
    onFilesSelected,
    accept,
    disabled,
    variant,
    value,
    setValue,
  });

  const handleSubmit = React.useCallback(() => {
    if (!disabled && hasText && onSubmit) {
      onSubmit(value);
      if (!isControlled) {
        setInternalValue('');
      }
    }
  }, [disabled, hasText, onSubmit, value, isControlled]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleClear = React.useCallback(() => {
    setValue('');
    if (variant === 'inline') {
      inputRef.current?.focus();
    } else {
      textareaRef.current?.focus();
    }
  }, [setValue, variant]);

  // Determine visual state
  const getState = (): VariantProps<typeof inputVariants>['state'] => {
    if (disabled) return 'disabled';
    if (isDragging) return 'dragover';
    if (hasAttachments) return 'attachments';
    if (hasText) return 'withText';
    if (isFocused) return 'focused';
    return 'default';
  };

  const contextValue = React.useMemo<AIInputContextValue>(
    () => ({
      value,
      hasText,
      isFocused,
      disabled,
      variant,
      uploadingFiles,
      hasAttachments,
      setValue,
      setIsFocused,
      handleSubmit,
      handleKeyDown,
      handleClear,
      onPaste,
      onRemoveFile,
      onCancelUpload,
      onRetryUpload,
      onRemoveAllFiles,
      isDragging,
      openFilePicker,
      onFilesSelected,
      textareaRef,
      inputRef,
    }),
    [
      value,
      hasText,
      isFocused,
      disabled,
      variant,
      uploadingFiles,
      hasAttachments,
      setValue,
      handleSubmit,
      handleKeyDown,
      handleClear,
      onPaste,
      onRemoveFile,
      onCancelUpload,
      onRetryUpload,
      onRemoveAllFiles,
      isDragging,
      openFilePicker,
      onFilesSelected,
    ],
  );

  const isDefault = variant === 'default';
  const state = getState();

  return (
    <AIInputContext.Provider value={contextValue}>
      <div
        ref={rootRef}
        data-slot="ai-input"
        data-dragging={isDragging}
        {...dropZoneProps}
        {...(onFilesSelected ? fileDropZoneA11yProps : {})}
        className={getAIInputShellClassName({ state, variant, className })}
      >
        <div
          data-slot="ai-input-inner"
          className={cn(
            inputVariants({ state, variant }),
            'relative',
            !isDefault && hasAttachments && 'flex-col items-stretch py-3',
            disabled && !isDefault ? 'bg-muted' : 'bg-background',
            'rounded-[inherit]',
            disabled && isDefault && 'bg-muted',
          )}
        >
          <DropOverlay
            isDefault={isDefault}
            dropText={dropText}
            active={isDragging && !disabled}
          />
          {children}
        </div>
      </div>
    </AIInputContext.Provider>
  );
}

AIInputRoot.displayName = 'AIInput.Root';

// =============================================================================
// AIInput.TextArea - The text input area
// =============================================================================

export interface AIInputTextAreaProps {
  /** Placeholder text */
  placeholder?: string;
  /** Additional class name */
  className?: string;
  /** Show clear button when text is present */
  showClearButton?: boolean;
}

function AIInputTextArea({
  placeholder = 'How can HUMAIN ONE help you today?',
  className,
  showClearButton = true,
}: AIInputTextAreaProps) {
  const ctx = useAIInputContext();
  const isInline = ctx.variant === 'inline';

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    ctx.setValue(e.target.value);
  };

  if (isInline) {
    return (
      <input
        ref={ctx.inputRef}
        data-slot="ai-input-text"
        type="text"
        value={ctx.value}
        onChange={handleChange}
        onFocus={() => ctx.setIsFocused(true)}
        onBlur={() => ctx.setIsFocused(false)}
        onKeyDown={ctx.handleKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={ctx.disabled}
        className={cn(
          'flex-1 min-w-0 bg-transparent text-sm leading-5 text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:text-muted-foreground',
          className,
        )}
      />
    );
  }

  return (
    <div
      data-slot="ai-input-text-area"
      className={cn(
        'flex gap-2',
        ctx.variant === 'compact' && !ctx.hasAttachments
          ? 'min-w-0 flex-1 items-center'
          : 'w-full items-start',
      )}
    >
      <textarea
        ref={ctx.textareaRef}
        data-slot="ai-input-textarea"
        value={ctx.value}
        onChange={handleChange}
        onPaste={ctx.onPaste}
        onFocus={() => ctx.setIsFocused(true)}
        onBlur={() => ctx.setIsFocused(false)}
        onKeyDown={ctx.handleKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={ctx.disabled}
        rows={1}
        className={cn(
          ctx.variant === 'compact' ? compactTextareaClasses : textareaClasses,
          className,
        )}
      />
      {showClearButton && ctx.variant !== 'compact' && (
        <button
          type="button"
          onClick={ctx.handleClear}
          aria-label="Clear input"
          disabled={!ctx.hasText || ctx.disabled}
          className={cn(
            'p-2 rounded-full hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring transition-colors duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none shrink-0',
            (!ctx.hasText || ctx.disabled) && 'invisible',
          )}
        >
          <X className="size-5 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

AIInputTextArea.displayName = 'AIInput.TextArea';

// =============================================================================
// AIInput.LeadingIcon - Leading icon for inline variant
// =============================================================================

export interface AIInputLeadingIconProps {
  /** Icon content (brand icon, spinner, avatar) */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
}

function AIInputLeadingIcon({ children, className }: AIInputLeadingIconProps) {
  return (
    <div
      data-slot="ai-input-leading-icon"
      aria-hidden="true"
      className={cn(
        'flex items-center justify-center shrink-0 size-9 rounded-xl bg-foreground/4 border border-border',
        className,
      )}
    >
      {children}
    </div>
  );
}

AIInputLeadingIcon.displayName = 'AIInput.LeadingIcon';

// =============================================================================
// AIInput.SendButton - The send/submit button
// =============================================================================

export interface AIInputSendButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Icon component to render */
  icon?: React.ComponentType<{ className?: string }>;
}

function AIInputSendButton({
  icon,
  className,
  ...props
}: AIInputSendButtonProps) {
  const ctx = useAIInputContext();
  const Icon = icon ?? ArrowUp;

  return (
    <Button
      type="button"
      data-slot="ai-input-send-button"
      size="icon-sm"
      onClick={ctx.handleSubmit}
      disabled={ctx.disabled || !ctx.hasText}
      aria-label="Send message"
      className={cn(
        'rounded-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:bg-accent disabled:text-muted-foreground',
        'transition-colors duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none',
        className,
      )}
      {...props}
    >
      <span
        data-slot="ai-input-send-icon"
        aria-hidden="true"
        className="flex size-4 shrink-0 items-center justify-center"
      >
        <Icon className="size-4" />
      </span>
    </Button>
  );
}

AIInputSendButton.displayName = 'AIInput.SendButton';

// =============================================================================
// AIInput.AttachButton - Attach file button
// =============================================================================

export interface AIInputAttachButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon component to render */
  icon: React.ComponentType<{ className?: string }>;
  /** Tooltip text */
  tooltip?: string;
}

function AIInputAttachButton({
  icon: Icon,
  tooltip,
  className,
  onClick,
  ...props
}: AIInputAttachButtonProps) {
  const ctx = useAIInputContext();

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e);
      } else if (ctx.onFilesSelected) {
        ctx.openFilePicker();
      }
    },
    [onClick, ctx.onFilesSelected, ctx.openFilePicker],
  );

  return (
    <button
      type="button"
      data-slot="ai-input-attach-button"
      onClick={handleClick}
      disabled={ctx.disabled}
      title={tooltip}
      aria-label={tooltip ?? 'Attach file'}
      className={cn(
        'p-2.5 min-h-11 min-w-11 flex items-center justify-center rounded-full text-secondary-foreground hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    >
      <Icon className="size-4" />
    </button>
  );
}

AIInputAttachButton.displayName = 'AIInput.AttachButton';

// =============================================================================
// AIInput.Actions - Container for action buttons
// =============================================================================

export interface AIInputActionsProps {
  /** Children (action buttons) */
  children?: React.ReactNode;
  /** Position of actions */
  position?: 'leading' | 'trailing';
  /** Additional class name */
  className?: string;
}

function AIInputActions({
  children,
  position = 'leading',
  className,
}: AIInputActionsProps) {
  return (
    <div
      data-slot="ai-input-actions"
      data-position={position}
      className={cn('flex items-center gap-2', className)}
    >
      {children}
    </div>
  );
}

AIInputActions.displayName = 'AIInput.Actions';

// =============================================================================
// AIInput.ActionBar - The bottom action bar (for default variant)
// =============================================================================

export interface AIInputActionBarProps {
  /** Children (leading actions, trailing actions, send button) */
  children?: React.ReactNode;
  /** Additional class name */
  className?: string;
}

function AIInputActionBar({ children, className }: AIInputActionBarProps) {
  const ctx = useAIInputContext();
  const isCompactRow = ctx.variant === 'compact' && !ctx.hasAttachments;

  return (
    <div
      data-slot="ai-input-action-bar"
      className={cn(
        'flex items-center',
        isCompactRow ? 'shrink-0 gap-3' : 'justify-between w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

AIInputActionBar.displayName = 'AIInput.ActionBar';

// =============================================================================
// AIInput.FilePreview - File attachments section
// =============================================================================

export interface AIInputFilePreviewProps {
  /** Additional class name */
  className?: string;
}

function AIInputFilePreview({ className }: AIInputFilePreviewProps) {
  const ctx = useAIInputContext();

  if (!ctx.hasAttachments) return null;

  const completedFiles = ctx.uploadingFiles.filter(
    (f) => f.status === 'complete',
  );

  // Compact/inline variants use the mobile file mini-card.
  if (ctx.variant !== 'default') {
    return (
      <div
        data-slot="ai-input-file-preview"
        className={cn('w-full flex flex-col gap-2', className)}
      >
        <div className="flex gap-2 w-full overflow-x-auto">
          {ctx.uploadingFiles.map((file) => (
            <FileUploadItem
              key={file.id}
              mobile
              fileName={file.name}
              fileExtension={file.fileExtension ?? file.name.split('.').pop()}
              progress={file.progress}
              status={file.status}
              className="shrink-0"
              onRemove={() => ctx.onRemoveFile?.(file.id)}
              onCancel={() => ctx.onCancelUpload?.(file.id)}
              onRetry={() => ctx.onRetryUpload?.(file.id)}
            />
          ))}
        </div>
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-card-foreground">
            {completedFiles.length} file
            {completedFiles.length !== 1 ? 's' : ''} added
          </span>
          <RemoveAllButton onClick={ctx.onRemoveAllFiles} />
        </div>
        <div className="w-full h-px bg-border" />
      </div>
    );
  }

  // Default variant uses the horizontal file mini-card.
  return (
    <div
      data-slot="ai-input-file-preview"
      className={cn('w-full flex flex-col gap-2', className)}
    >
      <div className="flex items-start gap-2 w-full overflow-x-auto">
        {ctx.uploadingFiles.map((file) => (
          <FileUploadItem
            key={file.id}
            fileName={file.name}
            fileSize={file.size}
            fileExtension={file.fileExtension ?? file.name.split('.').pop()}
            progress={file.progress}
            status={file.status}
            className="flex-1 min-w-0"
            onRemove={() => ctx.onRemoveFile?.(file.id)}
            onCancel={() => ctx.onCancelUpload?.(file.id)}
            onRetry={() => ctx.onRetryUpload?.(file.id)}
          />
        ))}
      </div>
      <div className="flex items-end justify-between w-full">
        <span className="text-xs text-card-foreground">
          {completedFiles.length} file
          {completedFiles.length !== 1 ? 's' : ''} added
        </span>
        <RemoveAllButton onClick={ctx.onRemoveAllFiles} />
      </div>
      <div className="w-full h-px bg-border" />
    </div>
  );
}

AIInputFilePreview.displayName = 'AIInput.FilePreview';

// =============================================================================
// Monolithic AIInput Component (Batteries-Included)
// =============================================================================

export interface AIInputProps extends VariantProps<typeof inputVariants> {
  /** Visual variant of the input */
  variant?: 'default' | 'compact' | 'inline';
  /** Placeholder text */
  placeholder?: string;
  /** Current input value */
  value?: string;
  /** Initial input value for uncontrolled usage */
  defaultValue?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Callback when message is submitted */
  onSubmit?: (message: string) => void;
  /** Files being uploaded */
  uploadingFiles?: UploadingFile[];
  /** Callback when file is removed */
  onRemoveFile?: (fileId: string) => void;
  /** Callback when file upload is cancelled */
  onCancelUpload?: (fileId: string) => void;
  /** Callback to retry a failed upload */
  onRetryUpload?: (fileId: string) => void;
  /** Callback to remove all files */
  onRemoveAllFiles?: () => void;
  /** Callback when files are selected (via drag-drop or file picker) */
  onFilesSelected?: (files: FileList) => void;
  /** Accepted file types (e.g. 'image/*,.pdf') */
  accept?: string;
  /** Allow multiple file selection */
  multiple?: boolean;
  /** Text shown in the drop overlay */
  dropText?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;

  // ==========================================================================
  // Slot Props (for customizing buttons)
  // ==========================================================================

  /** Actions rendered on the left side of the action bar, or before submit in inline mode */
  leadingActions?: React.ReactNode;
  /** Actions rendered on the right side, before submit button */
  trailingActions?: React.ReactNode;
  /** Custom submit button render function - receives onClick and disabled */
  submitButton?: SubmitButtonRenderFn;
  /** Custom default button render function (default variant only) - receives disabled */
  defaultButton?: DefaultButtonRenderFn;
  /** Leading icon for inline variant (brand icon, spinner, avatar). Required for inline, ignored for other variants. */
  leadingIcon?: React.ReactNode;
  /** Convert large/structured paste into a file attachment. */
  pasteAsFile?: PasteAsFileProp;
}

function AIInputDefault({
  variant = 'default',
  placeholder = 'How can HUMAIN ONE help you today?',
  value,
  defaultValue = '',
  onChange,
  onSubmit,
  uploadingFiles = [],
  onRemoveFile,
  onCancelUpload,
  onRetryUpload,
  onRemoveAllFiles,
  onFilesSelected,
  accept,
  multiple = true,
  dropText = 'Drop files to attach',
  disabled = false,
  className,
  // Slot props
  leadingActions,
  trailingActions,
  submitButton,
  defaultButton,
  leadingIcon,
  pasteAsFile,
}: AIInputProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [isFocused, setIsFocused] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const { isDragging, dropZoneProps } = useDropZone({
    onFilesSelected,
    accept,
    multiple,
    disabled,
    containerRef: rootRef,
  });

  const inputValue = value !== undefined ? value : internalValue;
  const hasText = inputValue.trim().length > 0;
  const hasAttachments = uploadingFiles.length > 0;
  const isControlled = value !== undefined;

  const isInline = variant === 'inline';
  const isDefault = variant === 'default';

  // Determine visual state
  const getState = (): VariantProps<typeof inputVariants>['state'] => {
    if (disabled) return 'disabled';
    if (isDragging) return 'dragover';
    if (hasAttachments) return 'attachments';
    if (hasText) return 'withText';
    if (isFocused) return 'focused';
    return 'default';
  };

  const setValue = React.useCallback(
    (next: string) => {
      if (onChange) {
        onChange(next);
      }
      if (!isControlled) {
        setInternalValue(next);
      }
    },
    [onChange, isControlled],
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setValue(e.target.value);
  };

  const { onPaste } = usePasteAsFile({
    pasteAsFile,
    onFilesSelected,
    accept,
    disabled,
    variant,
    value: inputValue,
    setValue,
  });

  const handleSubmit = () => {
    if (!disabled && hasText && onSubmit) {
      onSubmit(inputValue);
      if (!isControlled) {
        setInternalValue('');
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    }
    if (!isControlled) {
      setInternalValue('');
    }
    if (variant === 'inline') {
      inputRef.current?.focus();
    } else {
      textareaRef.current?.focus();
    }
  };

  // Shared: completed file count for file preview sections
  const completedCount = uploadingFiles.filter(
    (f) => f.status === 'complete',
  ).length;

  // Compact file preview (shared by compact + inline variants, skipped for default)
  const compactFilePreview = !isDefault && hasAttachments && (
    <div
      data-slot="ai-input-file-preview"
      className="w-full flex flex-col gap-2"
    >
      <div className="flex gap-2 w-full overflow-x-auto">
        {uploadingFiles.map((file) => (
          <FileUploadItem
            key={file.id}
            mobile
            fileName={file.name}
            fileExtension={file.fileExtension ?? file.name.split('.').pop()}
            progress={file.progress}
            status={file.status}
            className="shrink-0"
            onRemove={() => onRemoveFile?.(file.id)}
            onCancel={() => onCancelUpload?.(file.id)}
            onRetry={() => onRetryUpload?.(file.id)}
          />
        ))}
      </div>
      <div className="flex items-center justify-between w-full">
        <span className="text-xs text-card-foreground">
          {completedCount} file
          {completedCount !== 1 ? 's' : ''} added
        </span>
        <RemoveAllButton onClick={onRemoveAllFiles} />
      </div>
      <div className="w-full h-px bg-border" />
    </div>
  );

  // Inline variant
  if (isInline) {
    return (
      <div
        ref={rootRef}
        data-slot="ai-input"
        data-dragging={isDragging}
        {...dropZoneProps}
        {...(onFilesSelected ? fileDropZoneA11yProps : {})}
        className={getAIInputShellClassName({
          state: getState(),
          variant: 'inline',
          className,
        })}
      >
        <div
          data-slot="ai-input-inner"
          className={cn(
            inputVariants({ state: getState(), variant: 'inline' }),
            'relative bg-transparent',
            hasAttachments && 'flex-col items-stretch py-3',
          )}
        >
          <DropOverlay
            isDefault={isDefault}
            dropText={dropText}
            active={isDragging && !disabled}
          />
          {compactFilePreview}

          <div className="flex items-center gap-2 w-full">
            {leadingIcon ? (
              <AIInputLeadingIcon>{leadingIcon}</AIInputLeadingIcon>
            ) : null}

            <input
              ref={inputRef}
              data-slot="ai-input-text"
              type="text"
              value={inputValue}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label={placeholder}
              disabled={disabled}
              className="flex-1 min-w-0 bg-transparent text-sm leading-5 text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:text-muted-foreground"
            />

            {leadingActions}
            {trailingActions}

            <AIInputSubmitButton
              customButton={submitButton}
              onClick={handleSubmit}
              disabled={disabled || !hasText}
            />
          </div>
        </div>
      </div>
    );
  }

  // Compact variant
  if (!isDefault) {
    return (
      <div
        ref={rootRef}
        data-slot="ai-input"
        data-dragging={isDragging}
        {...dropZoneProps}
        {...(onFilesSelected ? fileDropZoneA11yProps : {})}
        className={getAIInputShellClassName({
          state: getState(),
          variant: 'compact',
          className,
        })}
      >
        <div
          data-slot="ai-input-inner"
          className={cn(
            inputVariants({ state: getState(), variant: 'compact' }),
            'relative bg-transparent',
            hasAttachments && 'flex-col items-stretch',
          )}
        >
          <DropOverlay
            isDefault={isDefault}
            dropText={dropText}
            active={isDragging && !disabled}
          />
          {compactFilePreview}

          <div
            data-slot="ai-input-text-area"
            className={cn(
              'flex gap-2',
              hasAttachments
                ? 'w-full items-start'
                : 'min-w-0 flex-1 items-center',
            )}
          >
            <textarea
              ref={textareaRef}
              data-slot="ai-input-textarea"
              value={inputValue}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
              onPaste={onPaste}
              placeholder={placeholder}
              aria-label={placeholder}
              disabled={disabled}
              rows={1}
              className={compactTextareaClasses}
            />
          </div>

          <div
            data-slot="ai-input-action-bar"
            className={cn(
              'flex items-center',
              hasAttachments ? 'justify-between w-full' : 'shrink-0 gap-3',
            )}
          >
            <div
              data-slot="ai-input-actions"
              className="flex items-center gap-2"
            >
              {leadingActions}
            </div>
            <div
              data-slot="ai-input-actions"
              className="flex items-center gap-2"
            >
              {trailingActions}
              <AIInputSubmitButton
                customButton={
                  hasText
                    ? submitButton
                    : defaultButton
                      ? () => defaultButton({ disabled })
                      : undefined
                }
                onClick={handleSubmit}
                disabled={disabled || !hasText}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-slot="ai-input"
      data-dragging={isDragging}
      {...dropZoneProps}
      {...(onFilesSelected ? fileDropZoneA11yProps : {})}
      className={getAIInputShellClassName({
        state: getState(),
        variant: 'default',
        className,
      })}
    >
      <div
        data-slot="ai-input-inner"
        className={cn(
          inputVariants({ state: getState(), variant: 'default' }),
          'relative bg-transparent',
        )}
      >
        <DropOverlay
          isDefault={isDefault}
          dropText={dropText}
          active={isDragging && !disabled}
        />
        {/* Attachments Section (above input, matching Figma layout) */}
        {hasAttachments && (
          <div
            data-slot="ai-input-file-preview"
            className="w-full flex flex-col gap-2"
          >
            <div className="flex items-start gap-2 w-full overflow-x-auto">
              {uploadingFiles.map((file) => (
                <FileUploadItem
                  key={file.id}
                  fileName={file.name}
                  fileSize={file.size}
                  fileExtension={
                    file.fileExtension ?? file.name.split('.').pop()
                  }
                  progress={file.progress}
                  status={file.status}
                  className="flex-1 min-w-0"
                  onRemove={() => onRemoveFile?.(file.id)}
                  onCancel={() => onCancelUpload?.(file.id)}
                  onRetry={() => onRetryUpload?.(file.id)}
                />
              ))}
            </div>

            <div className="flex items-end justify-between w-full">
              <span className="text-xs text-card-foreground">
                {completedCount} file
                {completedCount !== 1 ? 's' : ''} added
              </span>
              <RemoveAllButton onClick={onRemoveAllFiles} />
            </div>

            <div className="w-full h-px bg-border" />
          </div>
        )}

        <div
          data-slot="ai-input-text-area"
          className="flex items-start gap-2 w-full"
        >
          <textarea
            ref={textareaRef}
            data-slot="ai-input-textarea"
            value={inputValue}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            onPaste={onPaste}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled}
            rows={1}
            className={textareaClasses}
          />
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear input"
            disabled={!hasText || disabled}
            className={cn(
              'p-2 rounded-full hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring transition-colors duration-(--motion-duration-fast) ease-standard motion-reduce:transition-none shrink-0',
              (!hasText || disabled) && 'invisible',
            )}
          >
            <X className="size-5 text-muted-foreground" />
          </button>
        </div>

        <div
          data-slot="ai-input-action-bar"
          className="flex items-center justify-between w-full"
        >
          <div data-slot="ai-input-actions" className="flex items-center gap-2">
            {leadingActions}
          </div>

          <div data-slot="ai-input-actions" className="flex items-center gap-2">
            {trailingActions}
            <AIInputSubmitButton
              customButton={
                hasText
                  ? submitButton
                  : defaultButton
                    ? () => defaultButton({ disabled })
                    : undefined
              }
              onClick={handleSubmit}
              disabled={disabled || !hasText}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

AIInputDefault.displayName = 'AIInput';

// =============================================================================
// Export: Object.assign for dual-API
// =============================================================================

/**
 * AIInput - Dual-API component for AI chat input.
 *
 * Supports two usage patterns:
 *
 * 1. **Monolithic API (batteries-included)**:
 * ```tsx
 * <AIInput
 *   placeholder="Ask anything..."
 *   onSubmit={handleSubmit}
 *   leadingActions={<AIInput.ActionButton icon={Plus} />}
 * />
 * ```
 *
 * 2. **Composable API (full customization)**:
 * ```tsx
 * <AIInput.Root onSubmit={handleSubmit}>
 *   <AIInput.TextArea placeholder="Ask anything..." />
 *   <AIInput.ActionBar>
 *     <AIInput.Actions position="leading">
 *       <AIInput.AttachButton icon={Plus} tooltip="Add file" />
 *     </AIInput.Actions>
 *     <AIInput.Actions position="trailing">
 *       <AIInput.SendButton />
 *     </AIInput.Actions>
 *   </AIInput.ActionBar>
 *   <AIInput.FilePreview />
 * </AIInput.Root>
 * ```
 */
const AIInput = Object.assign(AIInputDefault, {
  Root: AIInputRoot,
  TextArea: AIInputTextArea,
  LeadingIcon: AIInputLeadingIcon,
  SendButton: AIInputSendButton,
  AttachButton: AIInputAttachButton,
  Actions: AIInputActions,
  ActionBar: AIInputActionBar,
  FilePreview: AIInputFilePreview,
  // Legacy compound components (also available on main export)
  ActionButton,
  SubmitButton,
  CompactActionButton,
});

export {
  // Compound components (also available as AIInput.X)
  ActionButton,
  AIInput,
  AIInputActionBar,
  AIInputActions,
  AIInputAttachButton,
  AIInputFilePreview,
  AIInputLeadingIcon,
  // New composable components
  AIInputRoot,
  AIInputSendButton,
  AIInputTextArea,
  CompactActionButton,
  inputVariants,
  SubmitButton,
  // Context hook for custom subcomponents
  useAIInputContext,
};

// Note: SubmitButtonRenderProps, SubmitButtonRenderFn, DefaultButtonRenderProps,
// and DefaultButtonRenderFn are exported at their definition sites above
